import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, switchMap, tap } from 'rxjs';
import { tapAllResponses } from 'shared/util-common';

import { GuidelineFilter, User } from '@treetop/shared/model';

import { GuidelinesFilterHttpService } from './guidelines-filter.http.service';
import { StorageService } from '../../../../../shared/util-common/src/lib/storage.service';

type ResourceTag = { id: string; label: string; color: string; backgroundColor: string };

type GuidelinesFilterState = {
  authors: User[];
  owners: User[];
  resourceTags: ResourceTag[];
  loadingAuthors: boolean;
  loadingOwners: boolean;
  loadingResourceTags: boolean;
  authorError: boolean;
  ownerError: boolean;
  resourceTagError: boolean;
  activeFilterCount: number;
  storeFilter: boolean;
  usableOnly: boolean;
  resourceTagsSource: ResourceTagsSource;

  filter: Partial<GuidelineFilter>;
};

const initialState: GuidelinesFilterState = {
  authors: [],
  owners: [],
  resourceTags: [],
  loadingAuthors: false,
  loadingOwners: false,
  loadingResourceTags: false,
  authorError: false,
  ownerError: false,
  resourceTagError: false,
  activeFilterCount: 0,
  storeFilter: false,
  usableOnly: false,
  resourceTagsSource: 'guidelines',

  filter: {},
};

/**
 * Options for initializing the GuidelinesFilterStore.
 * @property storePrefix - If set, the filter values will be stored with this prefix in the local storage.
 * @property usableOnly - If true, only usable (published) guidelines are considered when fetching resource tags.
 * @property resourceTagsSource - Endpoint to load the resource tags from. Smartbook readers aren't allowed to use the
 * guidelines endpoint, so the smartbook pages must use 'smartbook' (which only considers published guidelines).
 */
export type GuidelineFilterStoreInitOptions = {
  storePrefix?: string;
  usableOnly?: boolean;
  resourceTagsSource?: ResourceTagsSource;
};

export type ResourceTagsSource = 'guidelines' | 'smartbook';

export const GuidelinesFilterStore = signalStore(
  withState(initialState),
  withComputed(({ filter }) => ({
    activeFilterCount: computed(() => {
      let count = 0;

      count += filter().owners?.length ?? 0;
      count += filter().authors?.length ?? 0;
      count += filter().resourceTags?.length ?? 0;
      count += filter().showExpired ? 1 : 0;
      count += filter().showSoonToExpire ? 1 : 0;

      return count;
    }),
  })),
  withMethods((store) => {
    const http = inject(GuidelinesFilterHttpService);
    const storageService = inject(StorageService<{ filter: GuidelineFilter }>);

    const init = (options: GuidelineFilterStoreInitOptions) => {
      if (options.usableOnly) {
        patchState(store, { usableOnly: true });
      }

      if (options.resourceTagsSource) {
        patchState(store, { resourceTagsSource: options.resourceTagsSource });
      }

      getOwners();
      getAuthors();
      getResourceTags();

      if (options.storePrefix) {
        storageService.init(options.storePrefix);
        const storedFilter = storageService.getItem('filter');

        patchState(store, { storeFilter: true, filter: storedFilter ?? store.filter() });
      }
    };

    const getAuthors = rxMethod((trigger$: Observable<void>) =>
      trigger$.pipe(
        tap(() => patchState(store, { loadingAuthors: true })),
        switchMap(() => http.getAuthorsOfGuidelines()),
        tapAllResponses<User[], HttpErrorResponse>(
          (authors) => {
            patchState(store, {
              authors,
              loadingAuthors: false,
              authorError: false,
            });
          },
          () => {
            patchState(store, { loadingAuthors: false, authorError: true });
          },
        ),
      ),
    );

    const getOwners = rxMethod((trigger$: Observable<void>) =>
      trigger$.pipe(
        tap(() => patchState(store, { loadingOwners: true })),
        switchMap(() => http.getOwnersOfGuidelines()),
        tapAllResponses<User[], HttpErrorResponse>(
          (owners) => {
            patchState(store, {
              owners,
              loadingOwners: false,
            });
          },
          () => {
            patchState(store, { loadingOwners: false, ownerError: true });
          },
        ),
      ),
    );

    const getResourceTags = rxMethod((trigger$: Observable<void>) =>
      trigger$.pipe(
        tap(() => patchState(store, { loadingResourceTags: true })),
        switchMap(() =>
          store.resourceTagsSource() === 'smartbook'
            ? http.getUsedSmartbookResourceTags()
            : http.getUsedResourceTags({ usableOnly: store.usableOnly() }),
        ),
        tapAllResponses<ResourceTag[], HttpErrorResponse>(
          (resourceTags) => {
            patchState(store, {
              resourceTags,
              loadingResourceTags: false,
              resourceTagError: false,
            });
          },
          () => {
            patchState(store, { loadingResourceTags: false, resourceTagError: true });
          },
        ),
      ),
    );

    const patchFilter = (filter: Partial<GuidelineFilter>) => {
      patchState(store, {
        filter: {
          ...store.filter(),
          ...filter,
        },
      });

      if (store.storeFilter()) {
        storageService.setItem('filter', store.filter());
      }
    };

    const clearFilter = () => {
      patchFilter({
        searchText: '',
        guidelineStates: [],
        owners: [],
        authors: [],
        resourceTags: [],
        showSoonToExpire: false,
        showExpired: false,
      });
    };

    return {
      init,
      getAuthors,
      getOwners,
      getResourceTags,
      patchFilter,
      clearFilter,
    };
  }),
);
